import { Alert, Button, Col, Container, Row, Tabs, Tab, Form } from "react-bootstrap";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { LoginRequest, LoginProvider, ExternalLoginRequest, ProviderType } from "../../models";
import logo from "../../images/as-logo.png";
import { config } from "../../config";
import { GoogleLogin } from "./GoogleLogin";
import { useStoredState } from "../../hooks";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, useOidc, useProfile, useToasts } from "../../contexts";
import { CredentialResponse } from "./google";

export function Login() {
    const navigate = useNavigate();
    const isPublicComputer = true; // const { isPublicComputer, setIsPublicComputer } = useAuth()
    const { getLoginProviders, loginMFARequest, loginWithExternalProvider } = useOidc();
    const { errorToast } = useToasts();
    const {
        isAuthorized,
        isRefreshing,
        authState: { isLoggingIn },
    } = useAuth();
    const { userIsLoading } = useProfile();

    const [externalLoginErrorMessage, setExternalLoginErrorMessage] = useState<string>("");
    const [showNoProvidersErrorMessage, setShowNoProvidersErrorMessage] = useState<boolean>(false);
    const [loginProviders, setLoginProviders] = useState<LoginProvider[]>([]);
    const [emailAddress, setEmailAddress] = useStoredState<string>("login-email", "");
    const [password, setPassword] = useState<string>("");
    const showInternalProvider = loginProviders.some((p) => !p.isExternal && p.isActive);
    const showExternalProvider = loginProviders.some((p) => p.isExternal && p.isActive);
    const selectedTenantId = 1;

    useEffect(() => {
        if (isAuthorized && !userIsLoading && !isRefreshing) {
            navigate("/");
        }
    }, [isAuthorized, userIsLoading, navigate, isRefreshing]);

    const loadLoginProviders = useCallback(async () => {
        try {
            const response = await getLoginProviders();
            if (response) {
                setLoginProviders(response);
            }
        } catch (e: any) {
            console.log("login provider fetch error", e);
            setShowNoProvidersErrorMessage(true);
        }
    }, [getLoginProviders]);

    useEffect(() => {
        loadLoginProviders();
    }, [loadLoginProviders]);

    const handleSubmit = useCallback(
        async (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (!emailAddress.trim() || !password.trim()) return;
            const loginRequest: LoginRequest = {
                email: emailAddress,
                password,
                tenantId: selectedTenantId,
            };
            try {
                await loginMFARequest(loginRequest);
            } catch (e: any) {
                console.log("login error", e);
                errorToast(e);
            }
        },
        [emailAddress, loginMFARequest, password, errorToast]
    );

    const handleGoogleSignIn = useCallback(
        async (res: CredentialResponse) => {
            if (!res.clientId || !res.credential) return;
            const externalLoginRequest: ExternalLoginRequest = {
                token: res.credential,
                provider: ProviderType.Google,
                returnUrl: config.SSO_REDIRECT_URL,
                tenantId: selectedTenantId,
            };

            try {
                await loginWithExternalProvider(externalLoginRequest);
            } catch (e: any) {
                console.log("3rd party login error", e);
                setExternalLoginErrorMessage(e.error?.message);
            }
        },
        [loginWithExternalProvider, selectedTenantId]
    );

    const timeoutDuration = useMemo(() => {
        const minutes = config.AUTO_LOGOUT_ON_IDLE_MINUTES;
        const roundedMinutes = Math.round((minutes + Number.EPSILON) * 100) / 100;
        if (roundedMinutes < 60) {
            return roundedMinutes === 1 ? `${roundedMinutes} minute` : `${roundedMinutes} minutes`;
        }

        // round minutes / 60 to two decimal places
        const hours = minutes / 60;
        const roundedHours = Math.round((hours + Number.EPSILON) * 100) / 100;
        return roundedHours === 1 ? `${roundedHours} hour` : `${roundedHours} hours`;
    }, []);

    const autoLogoutMessage = useMemo(() => {
        if (config.AUTO_LOGOUT_ON_IDLE_MINUTES === 0) return null;

        if (isPublicComputer) {
            return (
                <Alert variant="info" className="mt-2">
                    You will be automatically logged out after {timeoutDuration} of inactivity.
                </Alert>
            );
        }

        return (
            <Alert variant="warning">
                You will not be automatically logged out after {timeoutDuration} of inactivity.
            </Alert>
        );
    }, [isPublicComputer, timeoutDuration]);

    const displayInternalProvider = useMemo(() => {
        if (showNoProvidersErrorMessage || !showInternalProvider) return null;

        return (
            <Form onSubmit={handleSubmit}>
                <Form.Group controlId="email" className="mb-2">
                    <Form.Label>
                        Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        type="email"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        required
                        autoFocus={!emailAddress}
                    />
                </Form.Group>
                <Form.Group controlId="password" className="mb-2">
                    <Form.Label>
                        Password <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoFocus={!!emailAddress}
                    />
                </Form.Group>
                <div className="d-flex justify-content-between align-items-center">
                    <Link to="/forgot-password">Forgot Password</Link>
                    <Button variant="primary" type="submit" disabled={isLoggingIn || !emailAddress || !password}>
                        {isLoggingIn ? "Logging in..." : "Login"}
                    </Button>
                </div>
            </Form>
        );
    }, [
        emailAddress,
        handleSubmit,
        isLoggingIn,
        password,
        setEmailAddress,
        showInternalProvider,
        showNoProvidersErrorMessage,
    ]);

    const providerDivider = useMemo(() => {
        if (!showNoProvidersErrorMessage && showInternalProvider && showExternalProvider && config.GOOGLE_CLIENT_ID) {
            return <hr className="my-2" />;
        }
        return null;
    }, [showExternalProvider, showInternalProvider, showNoProvidersErrorMessage]);

    const displayGoogleExternalProvider = useMemo(() => {
        if (showNoProvidersErrorMessage || !showExternalProvider || !config.GOOGLE_CLIENT_ID) return null;

        return (
            <div>
                <div className="d-flex justify-content-center">
                    <GoogleLogin clientId={config.GOOGLE_CLIENT_ID} handleGoogleSignIn={handleGoogleSignIn} />
                </div>
                <Alert className="mt-4" variant={"danger"} hidden={!externalLoginErrorMessage}>
                    {externalLoginErrorMessage}
                </Alert>
            </div>
        );
    }, [externalLoginErrorMessage, handleGoogleSignIn, showExternalProvider, showNoProvidersErrorMessage]);

    return (
        <Container>
            <Col xl={5} lg={6} md={8} className="mx-auto">
                <div className="text-center">
                    <img src={logo} alt="Logo" id="loginLogo" className="my-5" />
                </div>
                <Tabs>
                    <Tab eventKey="login" title="Login" className="py-3">
                        {showNoProvidersErrorMessage && (
                            <Row className="justify-content-center m-0">
                                <Col>
                                    <Alert className="mt-4" variant="danger">
                                        No Login Providers are configured. Please contact support.
                                    </Alert>
                                </Col>
                            </Row>
                        )}

                        {/* Keeping the public/private options commented out for now. We can add them back in if needed. */}
                        {/* <BootstrapForm.Select
                            value={isPublicComputer ? 1 : 0}
                            onChange={(e) => setIsPublicComputer(e.currentTarget.value === "1")}
                            className="mb-2"
                        >
                            <option value="1">Public Computer</option>
                            <option value="0">Private Computer</option>
                        </BootstrapForm.Select> */}

                        {displayInternalProvider}
                        {providerDivider}
                        {displayGoogleExternalProvider}
                        {autoLogoutMessage}
                    </Tab>
                </Tabs>
            </Col>
        </Container>
    );
}
